import { useMemo, useState } from 'react'
import Select, { MultiValue } from 'react-select'
import { useIntl } from 'react-intl'
import { KTSVG } from '../../../_metronic/helpers'
import AutocompleteSelect from '../../utils/Form/Widgets/AutocompleteSelect'
import Field from '../../utils/Form/Widgets/Field'
import { DateRangeInput } from '../../utils/Form/Widgets/DateRangeInput'
import { SearchWidget } from '../../utils/ResultViewer/ResultViewer'
import { DEFAULT_TASK_STATUS_FILTER, TASK_STATUS_FILTER_PARAM } from './taskFilterDefaults'

type StatusOption = {
  value: string
  label: string
}

const CompanyTasksFilters = (props: {
  search: SearchWidget[]
  filterParams: Record<string, any>
  setFilterParams: (name: string, value: any) => void
  applyFilter: () => void
  clearFilters: () => void
}) => {
  const intl = useIntl()
  const [filterValues, setFilterValues] = useState<any>(() => ({ ...props.filterParams }))

  const statusWidget = useMemo(
    () => props.search.find((widget) => widget.name === TASK_STATUS_FILTER_PARAM),
    [props.search]
  )

  const statusOptions: StatusOption[] = useMemo(() => {
    if (!statusWidget?.options) {
      return []
    }
    return Object.keys(statusWidget.options).map((key) => ({
      value: key,
      label: (statusWidget.options as any)[key],
    }))
  }, [statusWidget])

  const selectedStatuses: StatusOption[] = useMemo(() => {
    const raw =
      filterValues[TASK_STATUS_FILTER_PARAM] ?? props.filterParams[TASK_STATUS_FILTER_PARAM]
    const values: string[] = Array.isArray(raw) ? raw : raw ? [raw] : []
    return statusOptions.filter((option) => values.includes(option.value))
  }, [filterValues, props.filterParams, statusOptions])

  const updateLocalValue = (name: string, value: any) => {
    const next = { ...filterValues, [name]: value }
    setFilterValues(next)
    props.setFilterParams(name, value)
  }

  const handleStatusChange = (options: MultiValue<StatusOption>) => {
    const values = options.map((option) => option.value)
    updateLocalValue(TASK_STATUS_FILTER_PARAM, values)
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        return false
      }}
      className='d-inline'
    >
      <Field
        title={''}
        placeholder={intl.formatMessage({ id: 'ECOMMERCE.COMMON.SEARCH' })}
        type='search'
        name={'q'}
        autoComplete='off'
        onChange={(event) => {
          updateLocalValue('q', event.target.value)
        }}
      />
      <button
        type='button'
        className='btn btn-sm btn-primary me-3'
        data-kt-menu-trigger='click'
        data-kt-menu-placement='bottom-end'
        data-kt-menu-flip='top-end'
      >
        <KTSVG
          path='/media/icons/duotune/general/gen031.svg'
          className='svg-icon-6 svg-icon-muted me-1'
        />
        {intl.formatMessage({ id: 'FILTERS' })}
      </button>
      <div className='menu menu-sub menu-sub-dropdown w-250px w-md-350px' data-kt-menu='true'>
        <div className='px-7 py-5'>
          <div className='fs-5 text-dark fw-bolder'>
            {intl.formatMessage({ id: 'FILTERS.OPTIONS' })}
          </div>
        </div>

        <div className='separator border-gray-200'></div>
        {props.search.map((searchWidget) => {
          if (searchWidget.name === TASK_STATUS_FILTER_PARAM) {
            return (
              <div className='px-7 mb-3' key={searchWidget.name}>
                <label className='form-label fw-semibold'>{searchWidget.label}</label>
                <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                  <Select
                    isMulti
                    isClearable
                    className='w-100'
                    classNamePrefix='react-select'
                    options={statusOptions}
                    value={selectedStatuses}
                    placeholder={searchWidget.label}
                    onChange={handleStatusChange}
                    styles={{
                      menu: (base: any) => ({ ...base, zIndex: 5 }),
                      control: (base: any) => ({ ...base, minHeight: 38 }),
                    }}
                  />
                </div>
              </div>
            )
          }

          switch (searchWidget.type) {
            case 'select':
              if (
                searchWidget.attributes &&
                (searchWidget.attributes as any)['data-autocomplete-token']
              ) {
                return (
                  <div className='px-7' key={searchWidget.name}>
                    <AutocompleteSelect
                      title={searchWidget.label}
                      name={searchWidget.name}
                      autoCompleteToken={
                        (searchWidget.attributes as any)['data-autocomplete-token']
                      }
                      multiple={true}
                      defaultValue={filterValues[searchWidget.name] ?? 0}
                      onChange={(event: any) => {
                        updateLocalValue(searchWidget.name, event.target.value)
                      }}
                    />
                  </div>
                )
              }
              return null
            case 'daterange':
              return (
                <div className='px-7' key={searchWidget.name}>
                  <DateRangeInput
                    title={searchWidget.label}
                    name={searchWidget.name}
                    seperator='&'
                    onChange={(event: any) => {
                      updateLocalValue(searchWidget.name, event.target.value)
                    }}
                  />
                </div>
              )
            case 'text':
            default:
              return (
                <div className='px-7' key={searchWidget.name}>
                  <Field
                    title={searchWidget.label}
                    name={searchWidget.name}
                    autoComplete='off'
                    onChange={(event) => {
                      updateLocalValue(searchWidget.name, event.target.value)
                    }}
                  />
                </div>
              )
          }
        })}
        <div className='px-7 py-5'>
          <div className='d-flex justify-content-end'>
            <button
              type='reset'
              className='btn btn-sm btn-light btn-active-light-primary me-2'
              data-kt-menu-dismiss='true'
              onClick={() => {
                const defaults = {
                  [TASK_STATUS_FILTER_PARAM]: [...DEFAULT_TASK_STATUS_FILTER],
                }
                setFilterValues(defaults)
                props.clearFilters()
              }}
            >
              Reset
            </button>

            <button
              type='submit'
              className='btn btn-sm btn-primary'
              data-kt-menu-dismiss='true'
              onClick={() => props.applyFilter()}
            >
              Search
            </button>
          </div>
        </div>
      </div>
    </form>
  )
}

export default CompanyTasksFilters
